import { Card, CardContent } from '@/components/ui/card';
import { ErrorMessage } from '@/components/ui/error-message';
import { Spinner } from '@/components/ui/spinner';
import { useVendorPerformanceQuery, type VendorPerformance } from '@/lib/api/vendor-performance';
import { cn } from '@/lib/utils/cn';

/**
 * The vendor's track record, next to the checkpoint scores: did they
 * deliver on time, was work accepted first time, how many contract
 * shortfalls are open and were they answered in time. Counted in code from
 * the deliverable and deviation records; no AI figures. If the checkpoint
 * scores and this record disagree, that disagreement is itself a finding.
 */
function pct(rate: number | null): string {
  return rate === null ? '—' : `${Math.round(rate * 100)}%`;
}

export function VendorRecordCard({ engagementId }: { engagementId: string }) {
  const perf = useVendorPerformanceQuery(engagementId);
  return (
    <Card aria-labelledby="vendor-record-title">
      <CardContent className="p-0">
        <div className="border-b border-ink-100 px-5 py-3">
          <h2 id="vendor-record-title" className="text-h3 font-semibold text-ink-900">
            Delivery and deviation record
          </h2>
          <p className="text-meta text-ink-500">
            Counted from the deliverable and deviation records. It should agree with the checkpoint
            scores below; where it doesn&rsquo;t, look closer.
          </p>
        </div>
        {perf.isLoading ? (
          <div className="flex items-center gap-2 px-5 py-4 text-body-sm text-ink-500">
            <Spinner /> Loading the record…
          </div>
        ) : perf.error ? (
          <div className="px-5 py-4">
            <ErrorMessage error={perf.error} />
          </div>
        ) : perf.data ? (
          <Record data={perf.data} />
        ) : null}
      </CardContent>
    </Card>
  );
}

function Record({ data }: { data: VendorPerformance }) {
  const { delivery, acceptance, deviations } = data;
  const openTotal =
    deviations.open.Critical + deviations.open.High + deviations.open.Medium + deviations.open.Low;
  return (
    <dl className="grid grid-cols-1 divide-y divide-ink-100 md:grid-cols-3 md:divide-x md:divide-y-0">
      <Cell
        label="On-time delivery"
        value={pct(delivery.on_time_rate)}
        tone={delivery.on_time_rate !== null && delivery.on_time_rate < 0.8 ? 'at-risk' : undefined}
        sub={
          delivery.due === 0
            ? 'Nothing due yet'
            : `${delivery.on_time} on time · ${delivery.late} late · ${delivery.missing} not handed in, of ${delivery.due} due`
        }
      />
      <Cell
        label="Accepted first time"
        value={pct(acceptance.first_time_rate)}
        tone={
          acceptance.first_time_rate !== null && acceptance.first_time_rate < 0.7
            ? 'at-risk'
            : undefined
        }
        sub={
          acceptance.reviewed === 0
            ? 'Nothing reviewed yet'
            : `${acceptance.first_time_accepted} of ${acceptance.reviewed} reviewed deliverables`
        }
      />
      <Cell
        label="Open deviations"
        value={String(openTotal)}
        tone={
          deviations.open.Critical > 0
            ? 'critical'
            : deviations.open.High > 0
              ? 'at-risk'
              : undefined
        }
        sub={`${deviations.open.Critical} critical · ${deviations.open.High} high · ${deviations.open.Medium} medium · ${deviations.open.Low} low${
          deviations.response_deadline_missed > 0
            ? ` · ${deviations.response_deadline_missed} past response deadline`
            : ''
        } · ${deviations.resolved} resolved`}
      />
    </dl>
  );
}

function Cell({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: string;
  sub: string;
  tone?: 'at-risk' | 'critical';
}) {
  return (
    <div className="px-5 py-3">
      <dt className="text-meta font-semibold uppercase tracking-wider text-ink-500">{label}</dt>
      <dd
        className={cn(
          'mt-1 text-h2 font-semibold text-ink-900',
          tone === 'at-risk' && 'text-posture-at-risk',
          tone === 'critical' && 'text-posture-critical',
        )}
      >
        {value}
      </dd>
      <dd className="mt-0.5 text-meta text-ink-500">{sub}</dd>
    </div>
  );
}
